import React, { useEffect, useState, useContext } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { withRouter, RouteComponentProps } from "react-router";
import { Typography, Card } from "@material-ui/core";
import _ from "lodash";

import routes from "../../constants/routes";
import { Icon } from "../../components/icons/Icon";
import { PageHeader, PageRoot, TextField, Button } from "../../components";
import DetailSearchRelated from "../../components/details/DetailSearchRelated";
import { LegalEntity } from "../../constants/interfaces";
import { legalEntityActions } from "../../reducers/legalEntity";
import { searchTableData } from "../../utils/table";
import { LEGAL_ENTITY_AUTHORISATION_CONFIRMATION } from "../../constants/messages";
import { RxForm, FormValues, FormSection } from "../../components/Form";
import CheckboxField from "../../components/Fields/CheckboxField";
import FormTextField from "../../components/Fields/FormTextField";
import { SubmitButton } from "../../components/Form/SubmitButton";
import { ResetButton } from "../../components/Form/ResetButton";
import { OnUnSaveChange } from "../../components/Form/OnUnSaveChange";
import { ErrorMessage } from "../../components/Messages";
import { UIMessageContext } from "../../components/UIMessage";
import { emailValidator, websiteValidator } from "./validator";

const useStyles = makeStyles(theme => ({
  form: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column"
  },
  formCard: {
    padding: theme.spacing(4),
    maxWidth: theme.breakpoints.values.md
  },
  header: {
    alignItems: "center",
    display: "flex"
  },
  icon: {
    color: theme.palette.primary.light,
    marginRight: theme.spacing(2)
  },
  contentWrapper: {
    display: "flex",
    flexFlow: "row wrap",
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(4)
  },
  asideWrapper: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    marginLeft: 20
  },
  error: {
    marginBottom: theme.spacing(2)
  },
  buttonWrapper: {
    display: "flex",
    "&>button": {
      marginRight: "1.2em"
    }
  },
  fields: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  row: {
    display: "flex",
    "& > div": {
      flex: 1
    },
    "& > :first-child": {
      marginRight: 10
    }
  }
}));

type Props = RouteComponentProps & {
  classes?: any;
  error?: Error;
  legalEntity: LegalEntity;
  loading: boolean;
  getLegalEntity: (id: string) => Promise<LegalEntity>;
  updateLegalEntity: (
    legalEntity: LegalEntity,
    callback?: () => void
  ) => Promise<void>;
};

const MyLegalEntity: React.FC<Props> = props => {
  const classes = useStyles();
  const { legalEntity } = props;
  const [searchString, setSearchString] = useState<string>("");
  const legalEntityId = _.get(props, "match.params.id");
  const { showMessage } = useContext(UIMessageContext);

  useEffect(() => {
    if (legalEntityId) {
      props.getLegalEntity(legalEntityId);
      setSearchString("");
    }
  }, [legalEntityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const premises =
    legalEntity &&
    (!searchString
      ? legalEntity.associatedPremises
      : searchTableData(
          searchString,
          // Finds results matching either premises name or descriptor
          ["premisesName", "premisesDescriptor"],
          legalEntity.associatedPremises
        ));

  if (!legalEntity || _.isEmpty(legalEntity)) return null;
  return (
    <PageRoot>
      <PageHeader
        label={routes.MY_LEGAL_ENTITIES.label}
        breadcrumbLinks={[
          {
            label: "Legal Entity Information",
            route: routes.MY_LEGAL_ENTITIES.url
          },
          {
            active: true,
            label: legalEntity
              ? legalEntity.legalEntityName
              : "No Premise Found"
          }
        ]}
      />

      <Card className={classes.formCard}>
        <div className={classes.header}>
          <Icon className={classes.icon} fontSize="large" name="person" />
          <Typography color="textSecondary" component="h2" variant="h5">
            {legalEntity ? legalEntity.legalEntityName : "No Premise Found"}
          </Typography>
        </div>
        <RxForm
          className={classes.form}
          confirmMessage={LEGAL_ENTITY_AUTHORISATION_CONFIRMATION}
          onSubmit={value => {
            const legalEntity: LegalEntity = {
              ...props.legalEntity,
              addressDetails: {
                ...props.legalEntity.addressDetails,
                ...value.postalAddress,
                postalAndStreetAddessSame: value.postalAndStreetAddessSame
              },
              phoneNumber: value.phoneNumber,
              faxNumber: value.faxNumber || null,
              emailAddress: value.emailAddress,
              website: value.website || null
            };
            return props.updateLegalEntity(legalEntity, () => {
              showMessage("Update successful", "success");
            });
          }}
        >
          <OnUnSaveChange />
          <div className={classes.contentWrapper}>
            <div className={classes.fields}>
              <FormTextField
                required
                id="phoneNumber"
                name="phoneNumber"
                label="Phone Number"
                defaultValue={legalEntity.phoneNumber}
              />
              <FormTextField
                id="faxNumber"
                name="faxNumber"
                label="Fax Number"
                defaultValue={legalEntity.faxNumber}
              />
              <FormTextField
                required
                id="emailAddress"
                name="emailAddress"
                inputProps={emailValidator}
                label="Email"
                defaultValue={legalEntity.emailAddress}
              />
              <FormTextField
                label="Website"
                id="website"
                inputProps={websiteValidator}
                name="website"
                defaultValue={legalEntity.website}
              />
              <TextField
                id="streetAddressOtherDetails"
                label="Street Address Other detail"
                disabled
                value={
                  _.get(
                    legalEntity,
                    "addressDetails.streetAddressOtherDetails"
                  ) || ""
                }
              />
              <TextField
                disabled
                id="streetAddress"
                label="Street Address"
                value={_.get(legalEntity, "addressDetails.streetAddress") || ""}
              />
              <div className={classes.row}>
                <TextField
                  id="streetAddressSuburbOrRDNumber"
                  disabled
                  label="Suburb"
                  value={
                    _.get(
                      legalEntity,
                      "addressDetails.streetAddressSuburbOrRDNumber"
                    ) || ""
                  }
                />

                <TextField
                  id="streetAddressTownOrCity"
                  disabled
                  label="City"
                  value={
                    _.get(
                      legalEntity,
                      "addressDetails.streetAddressTownOrCity"
                    ) || ""
                  }
                />
              </div>
              <div className={classes.row}>
                <TextField
                  id="streetAddressCountry"
                  disabled
                  label="Country"
                  value={
                    _.get(legalEntity, "addressDetails.streetAddressCountry") ||
                    ""
                  }
                />

                <TextField
                  disabled
                  id="streetAddressPostCode"
                  label="Post Code"
                  value={
                    _.get(
                      legalEntity,
                      "addressDetails.streetAddressPostCode"
                    ) || ""
                  }
                />
              </div>
              <CheckboxField
                name="postalAndStreetAddessSame"
                label="Postal address is the same as street address"
                defaultValue={_.get(
                  legalEntity,
                  "addressDetails.postalAndStreetAddessSame"
                )}
              />
              <FormValues>
                {({ formValues }) => {
                  const { postalAndStreetAddessSame } = formValues;

                  if (postalAndStreetAddessSame) return null;

                  return (
                    <FormSection name="postalAddress">
                      <FormTextField
                        id="postalAddressOtherDetails"
                        name="postalAddressOtherDetails"
                        label="Postal Address Other detail"
                        destroyValueOnUnmount
                        defaultValue={_.get(
                          legalEntity,
                          "addressDetails.postalAddressOtherDetails"
                        )}
                      />

                      <FormTextField
                        required
                        destroyValueOnUnmount
                        id="postalAddress"
                        name="postalAddress"
                        label="Postal Address"
                        defaultValue={_.get(
                          legalEntity,
                          "addressDetails.postalAddress"
                        )}
                      />
                      <div className={classes.row}>
                        <FormTextField
                          destroyValueOnUnmount
                          id="postalAddressSuburbOrRDNumber"
                          name="postalAddressSuburbOrRDNumber"
                          label="Suburb"
                          defaultValue={_.get(
                            legalEntity,
                            "addressDetails.postalAddressSuburbOrRDNumber"
                          )}
                        />
                        <FormTextField
                          required
                          destroyValueOnUnmount
                          id="postalAddressTownOrCity"
                          name="postalAddressTownOrCity"
                          label="City"
                          defaultValue={_.get(
                            legalEntity,
                            "addressDetails.postalAddressTownOrCity"
                          )}
                        />
                      </div>
                      <div className={classes.row}>
                        <FormTextField
                          destroyValueOnUnmount
                          id="postalAddressCountry"
                          name="postalAddressCountry"
                          label="Country"
                          defaultValue={_.get(
                            legalEntity,
                            "addressDetails.postalAddressCountry"
                          )}
                        />
                        <FormTextField
                          required
                          destroyValueOnUnmount
                          id="postalAddressPostCode"
                          name="postalAddressPostCode"
                          label="Post Code"
                          defaultValue={_.get(
                            legalEntity,
                            "addressDetails.postalAddressPostCode"
                          )}
                        />
                      </div>
                    </FormSection>
                  );
                }}
              </FormValues>
            </div>

            <aside className={classes.asideWrapper}>
              <DetailSearchRelated
                icon="business"
                title="Premises"
                onSearch={(searchTerm: string) => {
                  setSearchString(searchTerm);
                }}
                results={
                  premises &&
                  premises.map(premise => ({
                    label: premise.premisesName,
                    url: `${routes.MY_PREMISE.url}${premise.cmAcmCaseIdentifier}`
                  }))
                }
              />
            </aside>
          </div>

          {props.error && (
            <ErrorMessage className={classes.error} error={props.error} />
          )}
          <div className={classes.buttonWrapper}>
            <SubmitButton label="Update" />
            <ResetButton label="Reset" />
            <Button
              onClick={() => {
                props.history.goBack();
              }}
              id="close"
            >
              Close
            </Button>
          </div>
        </RxForm>
      </Card>
    </PageRoot>
  );
};

const mapDispatchToProps = {
  getLegalEntity: legalEntityActions.getLegalEntity,
  updateLegalEntity: legalEntityActions.updateLegalEntity
};

const mapStateToProps = ({ legalEntity }: any, ownProps: Props) => {
  const {
    params: { id }
  }: any = ownProps.match;
  const currentLegalEntity: LegalEntity = legalEntity.legalEntities.find(
    (entity: LegalEntity) => entity.cmAcmCaseIdentifier === id
  );

  return {
    error: legalEntity.error,
    legalEntity: currentLegalEntity,
    loading: legalEntity.loading
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyLegalEntity));
